// SuperAppShell — chrome for the platform super-admin subtree (/super/*).
//
// Standalone from the tenant <AppShell />: separate top bar, separate
// sidebar, no module-switching layer, no dependency on tenant
// SidebarContext or the tenant authStore for branding. Mirrors the
// visual pattern of AppShell (top bar + fixed sidebar + main outlet) so
// the look stays consistent across tenant + super realms.

import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { SuperTopBar } from './SuperTopBar';
import { SuperSideNav } from './SuperSideNav';
import { EnvironmentBanner } from './EnvironmentBanner';
import { ToastContainer } from '../ui/Toast/Toast';
import { initErrorReporter } from '../../lib/errorReporter';
import { onLogoutEvent } from '../../api/client';
import { useSuperAdminStore } from '../../store/superAdminStore';
import styles from './SuperAppShell.module.css';

export function SuperAppShell(): JSX.Element {
  const setAnonymous = useSuperAdminStore((s) => s.setAnonymous);

  useEffect(() => {
    // Super shell — bind the reporter to the super-admin token slot so
    // log POSTs from the platform UI never depend on a tenant session.
    initErrorReporter({ realm: 'super' });
    // A super-admin refresh failure dispatches a realm-tagged logout
    // event; we react by clearing the super store so SuperProtectedRoute
    // bounces the user to /super/login on the next render.
    return onLogoutEvent('super', () => {
      setAnonymous();
    });
  }, [setAnonymous]);

  return (
    <div className={styles.shell}>
      <div data-print-hide>
        <SuperTopBar />
      </div>
      <div className={styles.body}>
        <div data-print-hide>
          <SuperSideNav />
        </div>
        <main className={styles.main}>
          <EnvironmentBanner />
          <Outlet />
        </main>
      </div>
      <div data-print-hide>
        <ToastContainer />
      </div>
    </div>
  );
}
